import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Inbox, Upload, ShieldCheck, Star, Crown, Layers3, Trash2, CheckCircle2, X, Download, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [pendingSubmissions, setPendingSubmissions] = useState(0);
  const loadCount = async () => {
    try {
      const { data } = await api.get("/admin/submissions/pending-count");
      setPendingSubmissions(data.count || 0);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadCount(); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</div>
        <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Directory control</h1>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 bg-slate-100" data-testid="admin-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="claims" data-testid="tab-claims">Claims</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="tab-submissions">
            Submissions{pendingSubmissions > 0 && <span className="ml-1.5 inline-flex items-center justify-center text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 rounded-full h-5 min-w-5 px-1.5">{pendingSubmissions}</span>}
          </TabsTrigger>
          <TabsTrigger value="upgrades" data-testid="tab-upgrades">Upgrades</TabsTrigger>
          <TabsTrigger value="imports" data-testid="tab-imports">Imports</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><Overview/></TabsContent>
        <TabsContent value="providers"><Providers/></TabsContent>
        <TabsContent value="leads"><Leads/></TabsContent>
        <TabsContent value="claims"><Claims/></TabsContent>
        <TabsContent value="submissions"><Submissions onChange={loadCount}/></TabsContent>
        <TabsContent value="upgrades"><UpgradeRequests/></TabsContent>
        <TabsContent value="imports"><Imports/></TabsContent>
      </Tabs>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/analytics/summary").then(({data}) => setStats(data)); }, []);
  if (!stats) return <div className="text-slate-500">Loading…</div>;
  const cards = [
    { label: "Total providers", value: stats.providers.total, icon: Users, color: "text-slate-900" },
    { label: "Verified", value: stats.providers.verified, icon: ShieldCheck, color: "text-emerald-600" },
    { label: "Featured", value: stats.providers.featured, icon: Star, color: "text-orange-600" },
    { label: "Unclaimed", value: stats.providers.unclaimed, icon: Layers3, color: "text-slate-500" },
    { label: "Total leads", value: stats.leads.total, icon: Inbox, color: "text-slate-900" },
    { label: "New leads", value: stats.leads.new, icon: Inbox, color: "text-[#002FA7]" },
    { label: "Claims pending", value: stats.claims.pending, icon: Crown, color: "text-[#FF5A00]" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((c) => {
        const I = c.icon;
        return (
          <div key={c.label} className="card-elevated p-5" data-testid={`stat-${c.label.replace(/\s+/g, '-').toLowerCase()}`}>
            <I size={18} className={c.color}/>
            <div className="text-xs uppercase tracking-widest text-slate-500 mt-3">{c.label}</div>
            <div className="font-display font-extrabold text-3xl text-slate-900 mt-1">{c.value}</div>
          </div>
        );
      })}
    </div>
  );
}

function Providers() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (tierFilter) params.set("tier", tierFilter);
    params.set("page_size", "50");
    const { data } = await api.get(`/admin/providers?${params.toString()}`);
    setItems(data.items || []);
    setTotal(data.total || 0);
    setLoading(false);
  };
  useEffect(() => { load(); }, [tierFilter]);

  const setField = async (id, field, value) => {
    try {
      await api.patch(`/providers/${id}`, { [field]: value });
      setItems((arr) => arr.map((it) => it.id === id ? { ...it, [field]: value } : it));
      toast.success("Updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const delProv = async (id) => {
    if (!confirm("Delete this provider permanently?")) return;
    try {
      await api.delete(`/admin/providers/${id}`);
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <Input placeholder="Search by name, slug, website, country…" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&load()} className="max-w-md" data-testid="admin-providers-search"/>
        <button onClick={load} className="btn-secondary"><RefreshCw size={14}/> Search</button>
        <Select value={tierFilter || "all"} onValueChange={(v)=>setTierFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="admin-tier-filter"><SelectValue placeholder="All tiers"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="Basic">Basic</SelectItem>
            <SelectItem value="Verified">Verified</SelectItem>
            <SelectItem value="Featured">Featured</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-slate-500 ml-auto">{total} providers</div>
      </div>
      {loading ? <div className="text-slate-500">Loading…</div> : (
        <div className="card-elevated overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>Company</Th><Th>Country</Th><Th>Tier</Th><Th>Verified</Th><Th>Featured</Th><Th>Status</Th><Th>Claim</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <React.Fragment key={p.id}>
                <tr className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3">
                    <Link to={`/solutions/${p.slug}`} className="font-semibold text-slate-900 hover:underline" data-testid={`admin-prov-${p.slug}`}>{p.name}</Link>
                    <div className="text-xs text-slate-500 font-mono">{p.slug}</div>
                  </td>
                  <td className="p-3 text-slate-600">{p.hq_country || "—"}</td>
                  <td className="p-3">
                    <Select value={p.tier} onValueChange={(v) => setField(p.id, "tier", v)}>
                      <SelectTrigger className="h-8 w-32"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Basic">Basic</SelectItem>
                        <SelectItem value="Verified">Verified</SelectItem>
                        <SelectItem value="Featured">Featured</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3">
                    <button onClick={() => setField(p.id, "verified", !p.verified)} className={`tag ${p.verified ? "tag-verified" : ""}`} data-testid={`admin-verified-${p.slug}`}>
                      {p.verified ? "✓ Verified" : "Not verified"}
                    </button>
                  </td>
                  <td className="p-3">
                    <button onClick={() => setField(p.id, "featured", !p.featured)} className={`tag ${p.featured ? "tag-featured" : ""}`} data-testid={`admin-featured-${p.slug}`}>
                      {p.featured ? "★ Featured" : "Standard"}
                    </button>
                  </td>
                  <td className="p-3">
                    <Select value={p.published} onValueChange={(v) => setField(p.id, "published", v)}>
                      <SelectTrigger className="h-8 w-28"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Published">Published</SelectItem>
                        <SelectItem value="Draft">Draft</SelectItem>
                        <SelectItem value="Hidden">Hidden</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-xs text-slate-600">{p.claim_status}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Link to={`/admin/providers/${p.id}/edit`}
                        className="text-xs font-semibold text-[#002FA7] hover:underline"
                        data-testid={`admin-edit-${p.slug}`}>
                        Edit
                      </Link>
                      <button onClick={() => delProv(p.id)} className="text-slate-400 hover:text-red-600 ml-2" data-testid={`admin-delete-${p.slug}`}>
                        <Trash2 size={16}/>
                      </button>
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left p-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">{children}</th>;
}

function Leads() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const load = async () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    const { data } = await api.get(`/admin/leads?${params.toString()}`);
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, [statusFilter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setItems((arr) => arr.map((l) => l.id === id ? { ...l, status } : l));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportLeads = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/export`, "_blank");
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 items-center flex-wrap">
        <Select value={statusFilter || "all"} onValueChange={(v)=>setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="leads-status-filter"><SelectValue placeholder="All statuses"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="New">New</SelectItem>
            <SelectItem value="Qualified">Qualified</SelectItem>
            <SelectItem value="Forwarded">Forwarded</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
            <SelectItem value="Won">Won</SelectItem>
            <SelectItem value="Lost">Lost</SelectItem>
          </SelectContent>
        </Select>
        <button className="btn-secondary ml-auto" onClick={exportLeads} data-testid="admin-export-leads"><Download size={14}/> Export CSV</button>
      </div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>Date</Th><Th>Contact</Th><Th>Company</Th><Th>Use case</Th><Th>Targets</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="p-3 text-slate-500 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="p-3">
                  <div className="font-semibold text-slate-900">{l.name}</div>
                  <div className="text-xs text-slate-500">{l.email}</div>
                </td>
                <td className="p-3 text-slate-600">{l.company || "—"}</td>
                <td className="p-3 text-slate-600 max-w-xs truncate" title={l.use_case}>{l.use_case || "—"}</td>
                <td className="p-3 text-slate-600 text-xs">{(l.selected_provider_ids || []).length} providers</td>
                <td className="p-3">
                  <Select value={l.status} onValueChange={(v)=>setStatus(l.id, v)}>
                    <SelectTrigger className="h-8 w-32"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Qualified">Qualified</SelectItem>
                      <SelectItem value="Forwarded">Forwarded</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                      <SelectItem value="Won">Won</SelectItem>
                      <SelectItem value="Lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center p-10 text-slate-400">No leads yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Claims() {
  const [items, setItems] = useState([]);
  const load = async () => {
    const { data } = await api.get(`/admin/claims`);
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/claims/${id}/${action}`, {});
      toast.success(action === "approve" ? "Claim approved" : "Claim rejected");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="card-elevated overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50"><tr>
          <Th>Date</Th><Th>Provider</Th><Th>Requester</Th><Th>Domain match</Th><Th>Status</Th><Th></Th>
        </tr></thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-slate-100">
              <td className="p-3 text-slate-500 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
              <td className="p-3">
                <Link to={`/solutions/${c.provider?.slug}`} className="font-semibold text-slate-900 hover:underline">{c.provider?.name}</Link>
                <div className="text-xs text-slate-500 font-mono">{c.provider?.website}</div>
              </td>
              <td className="p-3">
                <div className="text-slate-900">{c.requester_name}</div>
                <div className="text-xs text-slate-500">{c.requester_email} · {c.job_title}</div>
              </td>
              <td className="p-3">{c.domain_match ? <span className="tag tag-verified">Match</span> : <span className="tag">No</span>}</td>
              <td className="p-3"><span className={`tag ${c.status === "Approved" ? "tag-verified" : c.status === "Rejected" ? "" : "tag-featured"}`}>{c.status}</span></td>
              <td className="p-3">
                {c.status === "Pending" && (
                  <div className="flex gap-2">
                    <button onClick={()=>act(c.id, "approve")} className="text-xs font-semibold text-emerald-600 hover:underline inline-flex items-center gap-1" data-testid={`claim-approve-${c.id}`}><CheckCircle2 size={14}/> Approve</button>
                    <button onClick={()=>act(c.id, "reject")} className="text-xs font-semibold text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`claim-reject-${c.id}`}><X size={14}/> Reject</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={6} className="text-center p-10 text-slate-400">No claims yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function Submissions({ onChange }) {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/submissions?status=${tab}&page_size=100`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const approve = async (p) => {
    if (!window.confirm(`Approve and publish "${p.name}"? This will also email ${p.contact_email || "the submitter"} a claim invitation.`)) return;
    setBusyId(p.id);
    try {
      await api.post(`/admin/submissions/${p.id}/approve`);
      toast.success(`Published. Claim-invite email sent to ${p.contact_email || "submitter"}.`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (p) => {
    const note = window.prompt(`Reject "${p.name}" — optional admin note:`, "");
    if (note === null) return;
    setBusyId(p.id);
    try {
      await api.post(`/admin/submissions/${p.id}/reject`, { admin_notes: note });
      toast.success("Submission rejected.");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-submissions">
      <div className="flex items-center gap-2 mb-4">
        {[
          { k: "pending", label: "Pending review" },
          { k: "rejected", label: "Rejected" },
          { k: "all", label: "All" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            className={`text-xs font-semibold px-3 h-8 rounded-md border ${tab === t.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            data-testid={`sub-tab-${t.k}`}
          >{t.label}</button>
        ))}
      </div>

      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr><Th>Date</Th><Th>Company</Th><Th>Website</Th><Th>Submitter</Th><Th>Country</Th><Th>Status</Th><Th></Th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-slate-400">No submissions in this view.</td></tr>
            )}
            {!loading && items.map((p) => (
              <tr key={p.id} className="border-b border-slate-100" data-testid={`sub-row-${p.slug}`}>
                <td className="p-3 text-xs text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-3">
                  <Link to={`/admin/providers/${p.id}/edit`} className="font-semibold text-slate-900 hover:underline">{p.name}</Link>
                  <div className="text-[11px] text-slate-400 font-mono">/{p.slug}</div>
                </td>
                <td className="p-3 text-xs text-slate-500 font-mono break-all max-w-[220px]">{p.website || "—"}</td>
                <td className="p-3 text-xs">{p.contact_email || <span className="text-slate-400">—</span>}</td>
                <td className="p-3 text-xs">{p.hq_country || "—"}</td>
                <td className="p-3">
                  <span className={`tag border text-xs ${p.published === "Published" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : p.published === "Hidden" ? "bg-slate-200 text-slate-700 border-slate-300" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{p.published}</span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {p.published === "Draft" && (
                    <>
                      <button onClick={() => approve(p)} disabled={busyId === p.id} className="text-xs font-semibold text-emerald-700 hover:underline disabled:opacity-50" data-testid={`sub-approve-${p.slug}`}>
                        <CheckCircle2 size={14} className="inline -mt-0.5"/> Approve & publish
                      </button>
                      <button onClick={() => reject(p)} disabled={busyId === p.id} className="ml-3 text-xs font-semibold text-red-600 hover:underline disabled:opacity-50" data-testid={`sub-reject-${p.slug}`}>
                        <X size={14} className="inline -mt-0.5"/> Reject
                      </button>
                    </>
                  )}
                  <Link to={`/admin/providers/${p.id}/edit`} className="ml-3 text-xs font-semibold text-slate-700 hover:underline">Edit</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function UpgradeRequests() {
  const [items, setItems] = useState([]);
  const load = async () => {
    const { data } = await api.get("/admin/upgrade-requests");
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    try {
      await api.patch(`/admin/upgrade-requests/${id}`, { status });
      toast.success(`Request marked ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="card-elevated overflow-x-auto" data-testid="admin-upgrades">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50"><tr>
          <Th>Date</Th><Th>Provider</Th><Th>Requested tier</Th><Th>Trial</Th><Th>Message</Th><Th>Status</Th><Th></Th>
        </tr></thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
              <td className="p-3">
                <Link to={`/solutions/${r.provider?.slug}`} className="font-semibold text-slate-900 hover:underline">{r.provider?.name}</Link>
                <div className="text-xs text-slate-500 font-mono">Current: {r.provider?.tier}</div>
              </td>
              <td className="p-3"><span className="tag">{r.requested_tier}</span></td>
              <td className="p-3">
                {r.trial_requested ? <span className="tag tag-verified text-xs">14-day trial</span> : <span className="text-xs text-slate-400">—</span>}
              </td>
              <td className="p-3 text-slate-600 max-w-md">
                <div className="line-clamp-2 text-xs">{r.message || "—"}</div>
              </td>
              <td className="p-3">
                <Select value={r.status} onValueChange={(v)=>decide(r.id, v)}>
                  <SelectTrigger className="h-8 w-32" data-testid={`upgrade-status-${r.id}`}><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New">New</SelectItem>
                    <SelectItem value="Reviewing">Reviewing</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                    <SelectItem value="Deferred">Deferred</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="p-3 text-xs">
                {r.provider && (
                  <Link to={`/solutions/${r.provider.slug}`} className="text-[#002FA7] hover:underline font-semibold">Edit listing →</Link>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={7} className="text-center p-10 text-slate-400">No upgrade requests yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Imports() {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const previewRef = useRef(null);
  const runRef = useRef(null);

  const doPreview = async (f) => {
    if (!f) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/imports/preview", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setPreview({ ...data, file: f });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const doRun = async () => {
    if (!preview?.file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", preview.file);
      const { data } = await api.post("/admin/imports/run", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setResult(data);
      toast.success(`Imported ${data.imported} provider(s)`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="card-elevated p-6">
        <div className="font-display font-bold text-lg text-slate-900">Import providers (CSV)</div>
        <p className="text-sm text-slate-500 mt-1">Upload a CSV. Duplicate detection via slug & domain. Records default to Basic / Unclaimed / Published.</p>
        <input type="file" accept=".csv" ref={previewRef} onChange={(e)=>doPreview(e.target.files?.[0])} className="hidden" data-testid="import-file-input"/>
        <div className="mt-4 flex gap-2">
          <button onClick={()=>previewRef.current?.click()} className="btn-primary" disabled={busy} data-testid="import-pick-file"><Upload size={16}/> {busy ? "Working…" : "Choose CSV"}</button>
          {preview && <button onClick={doRun} className="btn-accent" disabled={busy} data-testid="import-run">Run import ({preview.row_count} rows)</button>}
        </div>
      </div>

      {preview && (
        <div className="card-elevated p-6">
          <div className="font-display font-semibold">Preview — {preview.row_count} rows</div>
          <div className="text-xs text-slate-500 mt-1">Columns: {(preview.columns || []).slice(0, 10).join(", ")}{preview.columns?.length > 10 ? "…" : ""}</div>
          <pre className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded-md p-3 overflow-x-auto max-h-64">{JSON.stringify(preview.sample, null, 2)}</pre>
        </div>
      )}

      {result && (
        <div className="card-elevated p-6">
          <div className="font-display font-bold text-lg">Import result</div>
          <div className="grid grid-cols-3 gap-4 mt-3">
            <Stat label="Imported" value={result.imported}/>
            <Stat label="Skipped duplicates" value={result.skipped_duplicates}/>
            <Stat label="Failed" value={result.failed}/>
          </div>
          {result.errors?.length > 0 && (
            <details className="mt-4">
              <summary className="text-sm text-red-600 cursor-pointer">View errors</summary>
              <pre className="mt-2 text-xs bg-red-50 border border-red-100 rounded-md p-3 overflow-x-auto max-h-64">{result.errors.join("\n")}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-display font-extrabold text-2xl text-slate-900 mt-1">{value}</div>
    </div>
  );
}
